/**
 * useBackgroundAwareInterval
 *
 * Drop-in replacement for raw `setInterval` polling that:
 *  - Pauses the interval while the Capacitor app is backgrounded.
 *  - Optionally fires the callback immediately on resume.
 *  - Prevents overlapping async invocations (waits for the previous
 *    promise to settle before scheduling the next tick).
 *  - Falls back to a plain interval on web/Tauri (no-op lifecycle).
 *
 * Usage:
 * ```ts
 * useBackgroundAwareInterval(async () => {
 *   await refreshNotifications();
 * }, { intervalMs: 30_000, runImmediately: true, fireOnResume: true });
 * ```
 *
 * @author TotalAccess Development Team
 * @date February 7, 2026
 */

import { useCallback, useEffect, useRef } from 'react';
import { lifecycleManager } from '@/lib/capacitorLifecycleManager';
import { isCapacitor } from '@/lib/platformUtils';

export interface BackgroundAwareIntervalOptions {
  /** Polling interval in milliseconds. */
  intervalMs: number;
  /** Fire the callback once on mount (default: false). */
  runImmediately?: boolean;
  /** Fire the callback once when the app resumes from background (default: true). */
  fireOnResume?: boolean;
  /**
   * When true (default), the interval is cleared while the app is backgrounded
   * and restarted on resume. When false, the interval keeps running regardless
   * of app state (useful only for critical, low-frequency heartbeats).
   */
  pauseWhileBackgrounded?: boolean;
  /** Skip the callback entirely when offline (default: false). */
  skipWhenOffline?: boolean;
  /**
   * When false, the interval is not started and the callback is not fired.
   * Use this for conditional polling (e.g. only while a modal is open or a
   * job is running). When `enabled` flips back to true, the interval restarts
   * (and `runImmediately` applies again if set).
   * Default: true.
   */
  enabled?: boolean;
  /** Deps that should re-create the interval (like useEffect deps). */
  deps?: React.DependencyList;
}

/**
 * Subscribe to lifecycle state changes without depending on React state.
 * Returns an unsubscribe function.
 */
function subscribeToBackgroundState(
  onBackground: () => void,
  onResume: () => void
): () => void {
  if (!isCapacitor()) {
    return () => {};
  }
  return lifecycleManager.subscribeToTypes(['pause', 'resume'], (event) => {
    if (event.type === 'pause') onBackground();
    else if (event.type === 'resume') onResume();
  });
}

export function useBackgroundAwareInterval(
  callback: () => void | Promise<void>,
  options: BackgroundAwareIntervalOptions
): void {
  const {
    intervalMs,
    runImmediately = false,
    fireOnResume = true,
    pauseWhileBackgrounded = true,
    skipWhenOffline = false,
    enabled = true,
    deps = [],
  } = options;

  // Keep latest callback in a ref so the interval doesn't reset on each render.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Track whether a previous invocation is still in flight to avoid overlap.
  const inFlightRef = useRef(false);

  // Track whether the app is currently backgrounded (lifecycle only).
  const isBackgroundedRef = useRef(false);

  // Grace period timer for background detection — prevents clearing the
  // interval when the app briefly loses focus to a camera overlay, barcode
  // scanner, or biometric prompt (which fire pause events on Capacitor).
  const backgroundGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const BACKGROUND_GRACE_PERIOD_MS = 3000;

  // Hold the current interval id so we can clear/recreate it.
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCurrentInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const tick = useCallback(async () => {
    if (inFlightRef.current) return;
    if (pauseWhileBackgrounded && isBackgroundedRef.current) return;
    if (skipWhenOffline && !lifecycleManager.isOnline()) return;

    inFlightRef.current = true;
    try {
      await callbackRef.current();
    } catch (error) {
      // Swallow errors here; callers are responsible for their own error UI.
      // We intentionally do not log to avoid spamming the dev error overlay
      // for transient background/offline failures.
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[useBackgroundAwareInterval] callback error swallowed', error);
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [pauseWhileBackgrounded, skipWhenOffline]);

  const startInterval = useCallback(() => {
    if (intervalRef.current !== null) return; // already running
    intervalRef.current = setInterval(() => {
      void tick();
    }, intervalMs);
  }, [intervalMs, tick]);

  useEffect(() => {
    // If polling is disabled, ensure any prior interval is cleared and do nothing.
    if (!enabled) {
      clearCurrentInterval();
      return clearCurrentInterval;
    }

    // On non-Capacitor platforms, behave like a normal interval.
    if (!isCapacitor()) {
      if (runImmediately) void tick();
      startInterval();
      return clearCurrentInterval;
    }

    // Ensure the lifecycle manager is initialized so background events fire.
    lifecycleManager.initialize();

    // Initial state snapshot.
    isBackgroundedRef.current = lifecycleManager.isBackground();

    const unsubscribe = subscribeToBackgroundState(
      () => {
        // Don't clear the interval immediately — the pause may be from a
        // camera overlay, barcode scanner, or biometric prompt. Wait for
        // the grace period before treating it as a real background event.
        if (backgroundGraceTimerRef.current) {
          clearTimeout(backgroundGraceTimerRef.current);
        }
        backgroundGraceTimerRef.current = setTimeout(() => {
          backgroundGraceTimerRef.current = null;
          isBackgroundedRef.current = true;
          if (pauseWhileBackgrounded) clearCurrentInterval();
        }, BACKGROUND_GRACE_PERIOD_MS);
      },
      () => {
        // Cancel any pending background grace timer — the app resumed
        // before the grace period elapsed (e.g. camera scan completed).
        if (backgroundGraceTimerRef.current) {
          clearTimeout(backgroundGraceTimerRef.current);
          backgroundGraceTimerRef.current = null;
        }
        const wasBackgrounded = isBackgroundedRef.current;
        isBackgroundedRef.current = false;
        // Only fire on resume if we actually went to background
        if (wasBackgrounded && fireOnResume) void tick();
        if (wasBackgrounded && pauseWhileBackgrounded) startInterval();
      }
    );

    if (!isBackgroundedRef.current) {
      if (runImmediately) void tick();
      startInterval();
    }

    return () => {
      unsubscribe();
      clearCurrentInterval();
      if (backgroundGraceTimerRef.current) {
        clearTimeout(backgroundGraceTimerRef.current);
        backgroundGraceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, runImmediately, fireOnResume, pauseWhileBackgrounded, skipWhenOffline, ...deps]);
}

export default useBackgroundAwareInterval;
